<template>
  <div>
    <!-- ── Attach proof modal ─────────────────────────────────────────────── -->
    <b-modal
      id="modal-attach-proof"
      ref="proofModal"
      centered
      no-close-on-backdrop
      no-close-on-esc
      size="lg"
      hide-footer
      hide-header
      @show="prefillProofForm"
      @hidden="resetForm"
    >
      <template #default="{}">
        <div class="modal-inner">
          <!-- Header -->
          <div class="modal-head">
            <div class="d-flex align-items-center" style="gap: 10px">
              <div class="modal-icon modal-icon-primary">
                <feather-icon icon="UploadCloudIcon" size="16" />
              </div>
              <div>
                <div class="modal-title">
                  Attach proof of payment — {{ selecteds.length }} beneficiar{{ selecteds.length === 1 ? "y" : "ies" }}
                </div>
                <div class="modal-sub">
                  {{ selectionBank ? selectionBank.name : "" }} · one reference and attachment will be recorded against all selected payments
                </div>
              </div>
            </div>
            <feather-icon
              icon="XIcon"
              size="16"
              class="cursor-pointer text-secondary"
              @click="$bvModal.hide('modal-attach-proof')"
            />
          </div>

          <div class="modal-body-inner">
            <!-- Summary -->
            <b-row class="mb-1">
              <b-col cols="6" md="4">
                <div class="weight-card wc-blue">
                  <div class="wc-label">Beneficiaries</div>
                  <div class="wc-value">{{ selecteds.length }}</div>
                </div>
              </b-col>
              <b-col cols="6" md="4">
                <div class="weight-card wc-green">
                  <div class="wc-label">Total amount</div>
                  <div class="wc-value">{{ fmtMoney(selectedTotal, selectionCurrency) }}</div>
                </div>
              </b-col>
              <b-col cols="12" md="4">
                <div class="weight-card wc-amber">
                  <div class="wc-label">Pay bank</div>
                  <div class="wc-value" style="font-size: 14px">{{ selectionBank ? selectionBank.name : "—" }}</div>
                </div>
              </b-col>
            </b-row>

            <!-- Selected beneficiaries -->
            <div class="section-head">Beneficiaries in this proof</div>
            <b-table
              ref="modalRecords"
              striped
              hover
              small
              class="position-relative payments-table mb-2"
              :items="selecteds"
              responsive
              :fields="modalColumns"
              primary-key="id"
              show-empty
              empty-text="No records selected"
              sticky-header="220px"
            >
              <template #cell(name)="data">
                <div class="d-flex align-items-center" style="gap: 8px">
                  <b-avatar size="24" :text="avatarText(data.item.beneficiary.name)" variant="light-primary" class="flex-shrink-0" />
                  <span style="font-size: 12px">{{ data.item.beneficiary.name }}</span>
                </div>
              </template>
              <template #cell(account_number)="data">
                <span class="font-monospace" style="font-size: 12px">{{ data.item.account.number }}</span>
              </template>
              <template #cell(amount)="data">
                {{ fmtMoney(data.item.amount, data.item.account.abbreviation) }}
              </template>
            </b-table>

            <!-- Proof form -->
            <div class="section-head">Proof of payment</div>
            <b-row>
              <b-col cols="12" md="6" class="mb-1">
                <label for="date_paid" class="reason-label">
                  Date Paid
                  <span class="required-star">*</span>
                </label>
                <b-form-input
                  name="date_paid"
                  id="date_paid"
                  v-model="deposit.datePaid"
                  type="date"
                  size="sm"
                  :state="formTouched && !deposit.datePaid ? false : null"
                  @input="formTouched = true"
                />
              </b-col>
              <b-col cols="12" md="6" class="mb-1">
                <label for="reference" class="reason-label">
                  Reference Number
                  <span class="required-star">*</span>
                  <span class="reason-hint">(from EDMS)</span>
                </label>
                <b-form-input
                  name="reference"
                  id="reference"
                  v-model="deposit.reference"
                  size="sm"
                  placeholder="e.g. EDMS/2026/00123"
                  :state="formTouched && deposit.reference && !deposit.reference.trim() ? false : null"
                  @input="formTouched = true"
                />
              </b-col>
              <b-col cols="12" md="6" class="mb-1">
                <label for="amount" class="reason-label">
                  Paid Amount
                  <span class="required-star">*</span>
                </label>
                <b-form-input
                  id="amount"
                  name="amount"
                  v-model="deposit.amount"
                  type="number"
                  step="0.001"
                  size="sm"
                  :placeholder="selectedTotal.toFixed(3)"
                  :state="formTouched && !paidAmount ? false : null"
                  @input="formTouched = true"
                />
              </b-col>
              <b-col cols="12" md="6" class="mb-1">
                <label for="bank_id" class="reason-label">
                  Paid Through Bank
                  <span class="required-star">*</span>
                </label>
                <b-form-select
                  id="bank_id"
                  name="bank_id"
                  v-model="deposit.bank_id"
                  size="sm"
                  :state="formTouched && !paidBankId ? false : null"
                  @change="formTouched = true"
                >
                  <b-form-select-option :value="null">
                    Select...
                  </b-form-select-option>
                  <b-form-select-option
                    v-for="bank in banks"
                    :key="bank.id"
                    :value="bank.id"
                  >
                    {{ bank.name }}
                  </b-form-select-option>
                </b-form-select>
              </b-col>
              <b-col cols="12" class="mb-1">
                <pdf-attachment-field
                  v-model="deposit.attachment"
                  label="Attachment"
                  hint="(PDF, optional, max 8MB)"
                />
              </b-col>
            </b-row>

            <small v-if="serverErrors" class="text-danger d-block mt-1">
              {{ Object.values(serverErrors).flat().join(' ') }}
            </small>

            {{ deposit }}
          </div>

          <!-- Footer -->
          <div class="modal-foot">
            <span class="foot-hint">Pay date, reference number, paid amount and bank are required.</span>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="saving"
              @click="$bvModal.hide('modal-attach-proof')"
            >
              Cancel
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              :disabled="saving"
              @click="submitProof"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              {{ saving ? "Submitting..." : "Submit Proof" }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
    <!-- ./Attach proof modal -->

    <!-- ── Excel import modal ─────────────────────────────────────────────── -->
    <b-modal
      id="modal-excel-import"
      ref="excelModal"
      centered
      no-close-on-backdrop
      no-close-on-esc
      size="xl"
      hide-footer
      hide-header
      @hidden="resetExcelForm"
    >
      <template #default="{}">
        <div class="modal-inner">
          <!-- Header -->
          <div class="modal-head">
            <div class="d-flex align-items-center" style="gap: 10px">
              <div class="modal-icon modal-icon-primary">
                <feather-icon icon="FileTextIcon" size="16" />
              </div>
              <div>
                <div class="modal-title">Import proof of payment from Excel</div>
                <div class="modal-sub">Upload a completed template to submit proof for many beneficiaries at once</div>
              </div>
            </div>
            <feather-icon
              icon="XIcon"
              size="16"
              class="cursor-pointer text-secondary"
              @click="$bvModal.hide('modal-excel-import')"
            />
          </div>

          <div class="modal-body-inner">
            <b-alert show variant="info" class="demo-data-alert mb-2">
              <div class="alert-body d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px; font-size: 12px">
                <span class="d-flex align-items-center" style="gap: 6px">
                  <feather-icon icon="InfoIcon" size="14" class="flex-shrink-0" />
                  Use the template so column names and Sale # match exactly. Each row is one beneficiary's proof of payment.
                </span>
                <b-button variant="outline-primary" size="sm" @click="downloadTemplate">
                  <feather-icon icon="DownloadIcon" size="12" class="mr-50" />
                  Download Sample Template
                </b-button>
              </div>
            </b-alert>

            <label class="reason-label">Completed Excel file (.xlsx, .xls)</label>
            <b-form-file
              ref="fileInput"
              type="file"
              v-model="excelFile"
              accept=".xlsx,.xls"
              placeholder="Choose a workbook or drop it here..."
              drop-placeholder="Drop workbook here..."
              size="sm"
              @change="convertFile"
            />

            <template v-if="uploading">
              <div class="text-center text-muted my-3">
                <b-spinner class="align-middle mr-50" small />
                <span>Reading workbook...</span>
              </div>
            </template>

            <template v-else-if="excelRows.length">
              <!-- Summary -->
              <b-row class="mt-2 mb-1">
                <b-col cols="4">
                  <div class="weight-card wc-blue">
                    <div class="wc-label">Rows parsed</div>
                    <div class="wc-value">{{ excelRows.length }}</div>
                  </div>
                </b-col>
                <b-col cols="4">
                  <div class="weight-card wc-green">
                    <div class="wc-label">Ready</div>
                    <div class="wc-value">{{ excelReadyRows.length }}</div>
                  </div>
                </b-col>
                <b-col cols="4">
                  <div class="weight-card wc-red">
                    <div class="wc-label">Issues</div>
                    <div class="wc-value">{{ excelIssueRows.length }}</div>
                  </div>
                </b-col>
              </b-row>

              <div class="section-head">Parsed rows</div>
              <b-table
                ref="excelRecords"
                striped
                hover
                small
                class="position-relative payments-table mb-2 excel-preview-table"
                :items="excelRows"
                responsive
                :fields="excelColumns"
                primary-key="key"
                show-empty
                empty-text="No rows parsed"
                sticky-header="320px"
              >
                <template #cell(beneficiary)="data">
                  <div v-if="data.item.matched" class="d-flex align-items-center" style="gap: 8px">
                    <b-avatar size="22" :text="avatarText(data.item.matched.beneficiary.name)" variant="light-primary" class="flex-shrink-0" />
                    <span style="font-size: 12px">{{ data.item.matched.beneficiary.name }}</span>
                  </div>
                  <span v-else-if="data.item.beneficiaryName" class="text-muted" style="font-size: 12px">
                    {{ data.item.beneficiaryName }}
                  </span>
                  <span v-else class="text-muted" style="font-size: 12px">—</span>
                </template>

                <template #cell(saleNumber)="data">
                  <span class="font-monospace" style="font-size: 12px">{{ data.item.saleNumber || "—" }}</span>
                </template>

                <template #cell(payDate)="data">
                  <span style="font-size: 12px">{{ data.item.payDate || "—" }}</span>
                </template>

                <template #cell(referenceNumber)="data">
                  <span style="font-size: 12px">{{ data.item.referenceNumber || "—" }}</span>
                </template>

                <template #cell(paidAmount)="data">
                  <span v-if="data.item.paidAmount !== null">
                    {{ fmtMoney(data.item.paidAmount, data.item.matched ? data.item.matched.account.abbreviation : "USD") }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>

                <template #cell(bankName)="data">
                  <span v-if="data.item.bankName">{{ data.item.bankName }}</span>
                  <span v-else class="text-muted">—</span>
                </template>

                <template #cell(status)="data">
                  <b-badge v-if="data.item.errors.length === 0" variant="light-success" class="status-badge">
                    <feather-icon icon="CheckIcon" size="10" class="mr-25" />
                    Ready
                  </b-badge>
                  <div v-else>
                    <b-badge variant="light-danger" class="status-badge">
                      <feather-icon icon="AlertTriangleIcon" size="10" class="mr-25" />
                      Issue
                    </b-badge>
                    <div class="excel-row-errors">{{ data.item.errors.join('; ') }}</div>
                  </div>
                </template>

                <template #cell(remove)="data">
                  <feather-icon
                    icon="Trash2Icon"
                    size="14"
                    class="cursor-pointer text-danger"
                    @click="removeExcelRow(data.item.key)"
                  />
                </template>
              </b-table>

              <pdf-attachment-field
                v-model="excelAttachment"
                label="Supporting attachment"
                hint="(optional — a combined bank advice covering this batch, PDF, max 8MB)"
              />
            </template>

            <small v-if="serverErrors" class="text-danger d-block mt-2">
              {{ Object.values(serverErrors).flat().join(' ') }}
            </small>
          </div>

          <!-- Footer -->
          <div class="modal-foot">
            <span class="foot-hint">
              {{ excelRows.length ? `${excelReadyRows.length} of ${excelRows.length} rows will be submitted.` : "Upload a workbook to preview rows." }}
            </span>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="excelSaving"
              @click="$bvModal.hide('modal-excel-import')"
            >
              Cancel
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              :disabled="excelSaving || excelReadyRows.length === 0"
              @click="submitExcelProof"
            >
              <b-spinner v-if="excelSaving" small class="mr-50" />
              {{ excelSaving ? "Submitting..." : `Submit Proof (${excelReadyRows.length})` }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
    <!-- ./Excel import modal -->

    <!-- ── Page header ─────────────────────────────────────────────────────── -->
    <div class="page-section-header mb-1">
      <div class="d-flex align-items-center" style="gap: 12px">
        <div class="page-header-icon">
          <feather-icon icon="UploadCloudIcon" size="20" />
        </div>
        <div>
          <h5 class="mb-25">Proof of Payment</h5>
          <small class="text-muted">
            Initiated payments awaiting proof — attach the pay date, EDMS reference and bank advice once funds have been transferred
          </small>
        </div>
      </div>
    </div>

    <b-alert v-if="usingMockData" show variant="warning" class="demo-data-alert mb-1">
      <div class="alert-body d-flex align-items-center" style="gap: 8px; font-size: 12px">
        <feather-icon icon="InfoIcon" size="14" class="flex-shrink-0" />
        Showing sample data — the pending-payments API is not reachable from this environment.
        Attaching proof on this screen will not persist.
      </div>
    </b-alert>

    <!-- ── Metric cards ────────────────────────────────────────────────────── -->
    <b-row class="mb-1">
      <b-col cols="6" md="4">
        <b-card class="metric-card metric-card-amber mb-1 mb-md-0">
          <div class="metric-icon metric-icon-amber">
            <feather-icon icon="ClockIcon" size="16" />
          </div>
          <div class="metric-label">Awaiting proof</div>
          <div class="metric-value text-warning">{{ pending.length.toLocaleString() }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="4">
        <b-card class="metric-card metric-card-blue mb-1 mb-md-0">
          <div class="metric-icon metric-icon-blue">
            <feather-icon icon="DollarSignIcon" size="16" />
          </div>
          <div class="metric-label">Total pending value</div>
          <div class="metric-value text-primary">{{ fmtMoney(totalPending, currencyAbbr) }}</div>
        </b-card>
      </b-col>
      <b-col cols="12" md="4">
        <b-card class="metric-card metric-card-red mb-0">
          <div class="metric-icon metric-icon-red">
            <feather-icon icon="AlertTriangleIcon" size="16" />
          </div>
          <div class="metric-label">Oldest pending</div>
          <div class="metric-value text-danger">{{ oldestPendingDays }} day{{ oldestPendingDays === 1 ? "" : "s" }}</div>
        </b-card>
      </b-col>
    </b-row>

    <!-- ── Table Container Card ────────────────────────────────────────────── -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="mb-0">Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50 select-size-sm"
            />
            <label class="mb-0">entries</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end" style="gap: 8px">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block"
                placeholder="Search beneficiary, sale #, account..."
                size="sm"
              />
              <b-button variant="outline-primary" size="sm" class="text-nowrap" @click="openExcelImport">
                <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
                Import from Excel
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Bulk action bar -->
      <div v-if="selecteds.length > 0" class="bulk-bar">
        <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />
        <span>
          {{ selecteds.length }} beneficiar{{ selecteds.length === 1 ? "y" : "ies" }} selected
          · {{ selectionBank ? selectionBank.name : "" }}
          · {{ fmtMoney(selectedTotal, selectionCurrency) }}
        </span>
        <b-button v-b-modal.modal-attach-proof variant="primary" size="sm">
          <span class="align-middle">Submit Proof</span>
          <feather-icon icon="UploadCloudIcon" class="ml-50" />
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="selecteds = []">Clear selection</b-button>
      </div>
      <div v-else class="m-2 d-flex align-items-center justify-content-end">
        <small class="text-muted">Select payments below — only payments from the same bank can be proved together</small>
      </div>

      <!-- Table -->
      <b-table
        ref="records"
        striped
        hover
        small
        class="position-relative payments-table"
        :items="fetch"
        responsive
        :fields="columns"
        primary-key="id"
        show-empty
        empty-text="No payments awaiting proof"
        :busy="loading"
        sticky-header="560px"
      >
        <template #table-busy>
          <div class="text-center text-muted my-3">
            <b-spinner class="align-middle mr-50" small />
            <span>Loading pending payments...</span>
          </div>
        </template>

        <template #head(id)>
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              type="checkbox"
              :checked="allOnPageSelected"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #185fa5"
              @change="toggleAllOnPage"
            >
          </div>
        </template>

        <template #cell(id)="data">
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              type="checkbox"
              :checked="selecteds.includes(data.item)"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #185fa5"
              @change="toggleRow(data.item)"
            >
          </div>
        </template>

        <template #cell(name)="data">
          <div class="d-flex align-items-center" style="gap: 8px">
            <span style="font-size: 12px; font-weight: 500">{{ data.item.beneficiary.name }}</span>
          </div>
        </template>

        <template #cell(market)="data">
          <span v-if="data.item.payable.market.code !== null">{{ data.item.payable.market.code.toUpperCase() }}: {{ data.item.payable.market.name.toUpperCase() }}</span>
          <span v-else>{{ data.item.payable.market.name.toUpperCase() }}</span>
        </template>

         <template #cell(account_name)="data">
          {{ data.item.account.name }}
        </template>

        <template #cell(account_number)="data">
          <span class="font-monospace text-muted" style="font-size: 12px">{{ data.item.account.number }}</span>
        </template>

        <template #cell(amount)="data">
          {{ fmtMoney(data.item.amount, data.item.account.abbreviation) }}
        </template>

        <template #cell(recoveries)="data">
          {{ fmtMoney(data.item.recoveries.reduce((sum, recovery) => sum + Number(recovery.amount), 0), data.item.account.abbreviation) }}
        </template>

        <template #cell(net)="data">
          {{ fmtMoney(data.item.amount - data.item.recoveries.reduce((sum, recovery) => sum + Number(recovery.amount), 0), data.item.account.abbreviation) }}
        </template>

        <template #cell(initiatedDate)="data">
          <span style="font-size: 12px">{{ data.item.initiatedDate | moment("DD/MM/YYYY") }}</span>
        </template>

        <template #cell(deposits)="data">
          {{ fmtMoney(data.item.deposits.reduce((sum, deposit) => sum + Number(deposit.amount), 0), data.item.account.abbreviation) }}
        </template>

        <template #cell(paidDate)="data">
          <span v-if="data.item.deposits.length > 0" style="font-size: 12px">{{ data.item.deposits[data.item.deposits.length - 1].paidDate | moment("DD/MM/YYYY") }}</span>
        </template>

        <template #cell(bank)="data">
          <span v-if="data.item.deposits.length > 0 && data.item.deposits[data.item.deposits.length - 1].bank !== null">
            {{ data.item.deposits[data.item.deposits.length - 1].bank  }}
          </span>
        </template>

        <template #cell(action)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            style="padding: 2px 9px; font-size: 11px"
            @click="openSingle(data.item)"
          >
            Submit Proof
          </b-button>
        </template>
      </b-table>

      <!-- Footer / pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ from }} to {{ to }} of {{ filteredRows.length }} entries</span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredRows.length"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
              <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <!-- ./Table Container Card -->
  </div>
</template>

<script>
import {
    BCard, BCardBody, BRow, BCol, BFormInput, BFormSelect, BFormSelectOption,
    BFormFile, BButton, BSpinner, BTable, BPagination, BBadge, BAvatar, BAlert,
    VBModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
    ref, onUnmounted, onMounted, computed,
} from '@vue/composition-api'
import store from '@/store'
import depositsStoreModule from '@/views/cromis/payments/deposit/depositsStoreModule'
import { avatarText } from '@core/utils/filter'
import { MOCK_PENDING_PAYMENTS, queryMockPendingPayments } from '@/views/cromis/payments/deposit/depositProofMockData'
import {
    parseProofWorkbook, buildPreviewRows, downloadProofTemplate,
} from '@/views/cromis/payments/deposit/depositExcelImport'
import PdfAttachmentField from '@/views/cromis/payments/deposit/PdfAttachmentField.vue'
import useDepositsList from '@/views/cromis/payments/deposit/useDepositsList'

export default {
    components: {
        BCard,
        BCardBody,
        BRow,
        BCol,
        BFormInput,
        BFormSelect,
        BFormSelectOption,
        BFormFile,
        BButton,
        BSpinner,
        BTable,
        BPagination,
        BBadge,
        BAvatar,
        BAlert,
        vSelect,
        VBModal,
        PdfAttachmentField,
    },
    directives: {
        'b-modal': VBModal,
    },
    setup(props, context) {
        const CROMIS_GROUP_STORE_MODULE_NAME = 'cromis-payments'
        if (!store.hasModule(CROMIS_GROUP_STORE_MODULE_NAME)) store.registerModule(CROMIS_GROUP_STORE_MODULE_NAME, depositsStoreModule)
        onUnmounted(() => {
            if (store.hasModule(CROMIS_GROUP_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_GROUP_STORE_MODULE_NAME)
        })

        const saving = ref(false)
        const serverErrors = ref(null)
        const usingMockData = ref(false)

        const banks = ref([])
        const pending = ref([])
        const selecteds = ref([])

        const uploading = ref(false)
        const fileInput = ref(null)
        const file = ref({
          originalName: null,
          document: null,
        })

        const modalColumns = ref([
            { key: 'name', label: 'Beneficiary', sortable: false },
            { key: 'saleNumber', label: 'Sale #', sortable: false },
            { key: 'account_number', label: 'Account', sortable: false },
            {
                key: 'amount', sortable: false, thClass: 'text-right', tdClass: 'text-right',
            },
        ])

        const excelColumns = ref([
            { key: 'beneficiary', label: 'Beneficiary', sortable: false },
            { key: 'saleNumber', label: 'Sale #', sortable: false },
            { key: 'payDate', label: 'Pay Date', sortable: false },
            { key: 'referenceNumber', label: 'Reference #', sortable: false },
            {
                key: 'paidAmount', label: 'Paid Amount', sortable: false, thClass: 'text-right', tdClass: 'text-right',
            },
            { key: 'bankName', label: 'Bank', sortable: false },
            { key: 'status', label: 'Status', sortable: false },
            {
                key: 'remove', label: '', sortable: false, thStyle: 'width:36px; min-width:36px',
            },
        ])

        const deposit = ref({
          id: null,
          datePaid: null,
          reference: null,
          amount: null,
          bank_id: null,
          attachment: null,
          payment_id: null,
        })

        // The real shape returned by GET payments hasn't been confirmed yet, so
        // every field is read defensively with fallbacks — this keeps the screen
        // from crashing while the exact backend contract gets nailed down.
        const normalizePayment = raw => ({
            id: raw.id,
            sale_id: raw.sale_id ?? raw.payable_id ?? raw.id,
            beneficiary: {
                id: raw.beneficiary?.id ?? raw.beneficiary_id ?? null,
                name: raw.beneficiary?.name ?? raw.name ?? raw.beneficiary_name ?? 'Unknown',
                code: raw.beneficiary?.code ?? raw.code ?? '',
            },
            saleNumber: raw.saleNumber ?? raw.sale_number ?? raw.number ?? '',
            market: {
                code: raw.market?.code ?? null,
                name: raw.market?.name ?? '',
            },
            account: {
                name: raw.account?.name ?? raw.account_name ?? '',
                number: raw.account?.number ?? raw.account_number ?? '',
                abbreviation: raw.account?.abbreviation ?? raw.currency?.abbreviation ?? raw.currency ?? 'USD',
            },
            bank: {
                id: raw.bank?.id ?? raw.bank_id ?? 0,
                name: raw.bank?.name ?? raw.bank_name ?? 'Unknown Bank',
            },
            amount: Number(raw.amount ?? raw.value ?? 0),
            initiatedAt: raw.initiatedAt ?? raw.created_at ?? raw.createdAt ?? new Date().toISOString(),
        })

        onMounted(async () => {
            await store.dispatch(`${CROMIS_GROUP_STORE_MODULE_NAME}/getBanks`)
                .then(response => { banks.value = response.data.banks })
                .catch(() => { banks.value = [] })
        })

        const {
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,
            loading,

            // Filters
            locationFilter,
            administrativeFilter,
            locationType,
            bank_id,
            currency_id,
            location_id,

            // UI
            // resolveUserRoleVariant,
            // resolveUserRoleIcon,
            resolveStatusVariant,
        } = useDepositsList()

        // ── Formatting helpers ───────────────────────────────────────────────
        const fmtMoney = (value, currency) => (value || 0).toLocaleString('en-US', {
            style: 'currency', currency: currency || 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3,
        })

        const currencyAbbr = computed(() => (pending.value.length ? pending.value[0].account.abbreviation : 'USD'))

        const totalPending = computed(() => pending.value.reduce((sum, item) => sum + item.amount, 0))

        const oldestPendingDays = computed(() => {
            if (!pending.value.length) return 0
            const oldest = pending.value.reduce((min, item) => Math.min(min, new Date(item.initiatedAt).getTime()), Date.now())
            return Math.max(0, Math.floor((Date.now() - oldest) / (1000 * 60 * 60 * 24)))
        })

        // ── Search / pagination ──────────────────────────────────────────────
        const filteredRows = computed(() => {
            if (!searchQuery.value.trim()) return pending.value
            return usingMockData.value
                ? queryMockPendingPayments(searchQuery.value)
                : pending.value.filter(p => {
                    const q = searchQuery.value.trim().toLowerCase()
                    return p.beneficiary.name.toLowerCase().includes(q)
                        || p.beneficiary.code.toLowerCase().includes(q)
                        || p.saleNumber.toLowerCase().includes(q)
                        || p.account.number.toLowerCase().includes(q)
                })
        })

        const pagedRows = computed(() => filteredRows.value.slice(from.value - 1 < 0 ? 0 : from.value - 1, to.value))

        // ── Selection (same bank required) ───────────────────────────────────
        const selectionBank = computed(() => (selecteds.value.length ? selecteds.value[0].bank : null))
        const selectionCurrency = computed(() => (selecteds.value.length ? selecteds.value[0].account.abbreviation : 'USD'))
        const selectedTotal = computed(() => selecteds.value.reduce((sum, item) => sum + item.amount, 0))

        const allOnPageSelected = computed(() => pagedRows.value.length > 0 && pagedRows.value.every(row => selecteds.value.includes(row)))

        const toggleRow = item => {
            const idx = selecteds.value.indexOf(item)
            if (idx > -1) {
                selecteds.value.splice(idx, 1)
                return
            }
            if (selecteds.value.length > 0 && selecteds.value[0].bank.id !== item.bank.id) {
                context.root.$swal({
                    icon: 'warning',
                    title: 'Different pay bank',
                    text: `Only payments from ${selecteds.value[0].bank.name} can be proved together. Clear the current selection first.`,
                    showConfirmButton: false,
                    timer: 2800,
                })
                return
            }
            selecteds.value.push(item)
        }

        const toggleAllOnPage = () => {
            if (allOnPageSelected.value) {
                selecteds.value = selecteds.value.filter(row => !pagedRows.value.includes(row))
                return
            }
            const targetBank = selecteds.value.length ? selecteds.value[0].bank.id : (pagedRows.value[0] && pagedRows.value[0].bank.id)
            const sameBankRows = pagedRows.value.filter(row => row.bank.id === targetBank)
            if (sameBankRows.length < pagedRows.value.length) {
                context.root.$swal({
                    icon: 'warning',
                    title: 'Mixed pay banks on this page',
                    text: 'Only rows matching the current selection\'s bank were selected.',
                    showConfirmButton: false,
                    timer: 2800,
                })
            }
            sameBankRows.forEach(row => {
                if (!selecteds.value.includes(row)) selecteds.value.push(row)
            })
        }

        const openSingle = item => {
            selecteds.value = [item]
            deposit.value.payment_id = item.id
            deposit.value.datePaid = item.deposits.length > 0? item.deposits[item.deposits.length - 1].paidDate : null
            deposit.value.reference = item.deposits.length > 0? item.deposits[item.deposits.length - 1].reference : null
            deposit.value.bank_id = item.deposits.length > 0? item.deposits[item.deposits.length - 1].bank_id : null
            deposit.value.amount = item.deposits.length > 0
                                ? item.deposits[item.deposits.length - 1].amount
                                : item.amount - item.recoveries.reduce((accum, recovery) => accum + recovery.amount, 0)
            context.root.$bvModal.show('modal-attach-proof')
        }

        // ── Proof form (manual / multi-select) ───────────────────────────────
        const payDate = ref(null)
        const referenceNumber = ref('')
        const paidAmount = ref(null)
        const paidBankId = ref(null)
        const attachmentFile = ref(null)
        const formTouched = ref(false)

        const isAttachmentValid = computed(() => !deposit.value.attachment
            || (deposit.value.attachment.type === 'application/pdf' && deposit.value.attachment.size <= 8 * 1024 * 1024))

        const resetForm = () => {
          deposit.value.id = null
          deposit.value.datePaid = null
          deposit.value.reference = null
          deposit.value.amount = null
          deposit.value.bank_id = null
          deposit.value.attachment = null
          deposit.value.payment_id = null
        }

        const prefillProofForm = () => {
            if (!deposit.value.amount) deposit.value.amount = selectedTotal.value.toFixed(3)
        }

        const validateProofForm = () => {
            formTouched.value = true

            return !!deposit.value.datePaid && deposit.value.reference && !!deposit.value.reference.trim() && !!deposit.value.amount
                && !!deposit.value.bank_id && isAttachmentValid.value
        }

        const submitProof = async () => {
            if (!validateProofForm()) return

            saving.value = true
            serverErrors.value = null

            if(deposit.value.id === null){
              await createDeposit()
            }
            else{
              await updateDeposit()
            }
        }

        const createDeposit = async () => {
          await store.dispatch(`${CROMIS_GROUP_STORE_MODULE_NAME}/createDeposit`, deposit.value)
                      .then(response => {
                        saving.value = false
                        context.root.$bvModal.hide('modal-attach-proof')
                        context.root.$swal({
                            icon: 'success',
                            text: 'Proof of payment recorded successfully!',
                            showConfirmButton: true,
                            timer: 4000,
                            customClass: { confirmButton: 'btn btn-primary' },
                            buttonsStyling: false,
                        })

                        resetForm()
                        refetch()
                      })
                      .catch(error => {
                        saving.value = false
                        context.root.$bvModal.hide('modal-attach-proof')
                        if (error.response && error.response.status === 422) {
                            serverErrors.value = error.response.data.errors
                        } else {
                            context.root.$swal({
                                icon: 'error',
                                title: 'Server Error',
                                text: 'Something went wrong. See tech support',
                                showConfirmButton: true,
                                customClass: { confirmButton: 'btn btn-danger' },
                                buttonsStyling: false,
                            })
                        }
                      })
        }

        const updateDeposit = async () => {
          await store.dispatch(`${CROMIS_GROUP_STORE_MODULE_NAME}/updateDeposit`, { id: deposit.value.id, data: deposit.value})
                      .then(response => {
                        saving.value = false
                        context.root.$bvModal.hide('modal-attach-proof')

                        context.root.$swal({
                            icon: 'success',
                            text: 'Proof of payment recorded successfully!',
                            showConfirmButton: true,
                            timer: 4000,
                            customClass: { confirmButton: 'btn btn-primary' },
                            buttonsStyling: false,
                        })

                        resetForm()
                        refetch()
                      })
                      .catch(error => {
                        saving.value = false
                        context.root.$bvModal.hide('modal-attach-proof')

                        if (error.response && error.response.status === 422) {
                            serverErrors.value = error.response.data.errors
                        } else {
                            context.root.$swal({
                                icon: 'error',
                                title: 'Server Error',
                                text: 'Something went wrong. See tech support',
                                showConfirmButton: true,
                                customClass: { confirmButton: 'btn btn-danger' },
                                buttonsStyling: false,
                            })
                        }
                      })
        }

        // ── Proof form (Excel bulk import) ───────────────────────────────────
        const excelFile = ref(null)
        const excelParsing = ref(false)
        const excelSaving = ref(false)
        const excelRows = ref([])
        const excelAttachment = ref(null)

        const excelReadyRows = computed(() => excelRows.value.filter(r => r.errors.length === 0))
        const excelIssueRows = computed(() => excelRows.value.filter(r => r.errors.length > 0))

        const openExcelImport = () => {
            context.root.$bvModal.show('modal-excel-import')
        }

        const resetExcelForm = () => {
            excelFile.value = null
            excelRows.value = []
            excelParsing.value = false
            excelAttachment.value = null
            serverErrors.value = null
        }

        const onExcelFileChange = file => {
            excelRows.value = []
            if (!file) return

            excelParsing.value = true
            parseProofWorkbook(file)
                .then(rawRows => {
                    excelRows.value = buildPreviewRows(rawRows, pending.value, banks.value)
                })
                .catch(() => {
                    context.root.$swal({
                        icon: 'error',
                        title: 'Could not read file',
                        text: 'That file could not be read as an Excel workbook. Please use the template and try again.',
                        showConfirmButton: true,
                        customClass: { confirmButton: 'btn btn-danger' },
                        buttonsStyling: false,
                    })
                })
                .finally(() => {
                    excelParsing.value = false
                })
        }

        const convertFile = e => {
          file.value = {
              document: null,
              originalName: null,
          }

          const rawFile = e.target.files[0]

          file.value.originalName = rawFile.name

          serverErrors.value = null
          uploading.value = false

          if (file.value.size > 30 && file.value.unit === 'MB') {
              serverErrors.value = {
                  fileInput: [
                      'File size exceeded (Max 30MB)',
                  ],
              }
          } else {
              const name = file.value.originalName
              if (!['csv', 'xls', 'xlsx'].includes(name.substring(name.indexOf('.') + 1))) {
                  serverErrors.value = {
                      fileInput: [
                          'Invalid file type, required excel file (csv, xlsx or xls)',
                      ],
                  }
              }
          }

          const reader = new FileReader()

          reader.onloadend = async () => {
              file.value.document = await reader.result

              if (!serverErrors.value || !serverErrors.value.fileInput) {
                  // file.value.location_id = selections.value[selections.value.length - 1].model.id
                  uploading.value = true
                  await store.dispatch(`${CROMIS_GROUP_STORE_MODULE_NAME}/importPayments`, file.value)
                      .then(response => {
                          refetch()
                          uploading.value = false

                          file.value = {
                            document: null,
                            originalName: null,
                          }

                          context.root.$bvModal.hide('modal-excel-import')

                      })
                      .catch(error => {
                          uploading.value = false
                          context.root.$swal({
                              icon: 'error',
                              title: `${error.response.status} ${error.response.statusText}`,
                              text: error.response.data.message,
                              showConfirmButton: true,
                              customClass: {
                                  confirmButton: 'btn btn-outline-danger',
                              },
                              buttonsStyling: false,
                          })
                      })
              } else {
                  uploading.value = false
              }
          }

          reader.readAsDataURL(rawFile)
      }

        const removeExcelRow = key => {
            excelRows.value = excelRows.value.filter(r => r.key !== key)
        }

        const downloadTemplate = () => {
            downloadProofTemplate(pending.value, banks.value)
        }

        const submitExcelProof = async () => {
            if (excelReadyRows.value.length === 0) return

            excelSaving.value = true
            serverErrors.value = null

            const formData = new FormData()
            formData.append('items', JSON.stringify(excelReadyRows.value.map(r => ({
                payment_id: r.matched.id,
                pay_date: r.payDate,
                reference_number: r.referenceNumber,
                paid_amount: r.paidAmount,
                bank_id: r.bankId,
            }))))
            if (excelAttachment.value) formData.append('attachment', excelAttachment.value)

            await store.dispatch(`${CROMIS_GROUP_STORE_MODULE_NAME}/bulkAttachProof`, formData)
                .then(() => {
                    const provedIds = excelReadyRows.value.map(r => r.matched.id)
                    pending.value = pending.value.filter(item => !provedIds.includes(item.id))
                    context.root.$bvModal.hide('modal-excel-import')

                    context.root.$swal({
                        icon: 'success',
                        text: `Proof of payment recorded for ${provedIds.length} beneficiaries!`,
                        showConfirmButton: true,
                        timer: 4000,
                        customClass: { confirmButton: 'btn btn-primary' },
                        buttonsStyling: false,
                    })
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        serverErrors.value = error.response.data.errors
                    } else {
                        context.root.$swal({
                            icon: 'error',
                            title: 'Server Error',
                            text: 'Something went wrong. See tech support',
                            showConfirmButton: true,
                            customClass: { confirmButton: 'btn btn-danger' },
                            buttonsStyling: false,
                        })
                    }
                })

            excelSaving.value = false
        }

        return {
            // Table
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,

            loading,
            saving,
            serverErrors,
            usingMockData,
            banks,
            pending,
            selecteds,
            modalColumns,
            excelColumns,
            fmtMoney,
            avatarText,
            currencyAbbr,
            totalPending,
            oldestPendingDays,
            filteredRows,
            selectionBank,
            selectionCurrency,
            selectedTotal,
            allOnPageSelected,
            toggleRow,
            toggleAllOnPage,
            openSingle,
            payDate,
            referenceNumber,
            paidAmount,
            paidBankId,
            attachmentFile,
            formTouched,
            isAttachmentValid,
            resetForm,
            prefillProofForm,
            submitProof,
            convertFile,
            uploading,
            fileInput,

            deposit,

            // Excel bulk import
            excelFile,
            excelParsing,
            excelSaving,
            excelRows,
            excelAttachment,
            excelReadyRows,
            excelIssueRows,
            openExcelImport,
            resetExcelForm,
            onExcelFileChange,
            removeExcelRow,
            downloadTemplate,
            submitExcelProof,
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.page-section-header {
  padding-bottom: 0.85rem;
  border-bottom: 0.5px solid #ebe9f1;

  .page-header-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: linear-gradient(135deg, #e6f1fb, #d9ecf9);
    color: #185fa5;
  }
}

.demo-data-alert {
  border-radius: 8px;
  ::v-deep .alert-body { padding: 0.6rem 1rem; }
}

// ── Metric cards ───────────────────────────────────────────────────────────
.metric-card {
  border-radius: 0.6rem;
  border-top: 3px solid transparent;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;

  &:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06); }

  ::v-deep .card-body { padding: 1rem 1.1rem; }

  &.metric-card-blue  { border-top-color: #185fa5; }
  &.metric-card-amber { border-top-color: #ef9f27; }
  &.metric-card-red   { border-top-color: #e24b4a; }

  .metric-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
    &.metric-icon-blue  { background: #e6f1fb; color: #185fa5; }
    &.metric-icon-amber { background: #faeeda; color: #854f0b; }
    &.metric-icon-red   { background: #fcebeb; color: #a32d2d; }
  }

  .metric-label {
    font-size: 11px; color: #b9b9c3;
    text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 5px;
  }
  .metric-value { font-size: 22px; font-weight: 500; line-height: 1.2; }
}

// ── Bulk bar ───────────────────────────────────────────────────────────────
.bulk-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 9px 16px;
  background: #e6f1fb; border-bottom: 0.5px solid #b8d7f0;
  font-size: 12px; color: #185fa5;

  span { margin-right: auto; }
}

// ── Modal ──────────────────────────────────────────────────────────────────
.modal-inner { overflow: hidden; }

.modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 0.5px solid #ebe9f1; background: #fafafa;

  .modal-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    &.modal-icon-primary { background: #e6f1fb; color: #185fa5; }
  }

  .modal-title { font-size: 14px; font-weight: 600; color: #5e5873; }
  .modal-sub   { font-size: 11px; color: #b9b9c3; margin-top: 1px; }
}

.modal-body-inner { padding: 18px 18px 4px; }

.modal-foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 12px 18px;
  border-top: 0.5px solid #ebe9f1; background: #fafafa;
  .foot-hint { flex: 1; font-size: 11px; color: #b9b9c3; }
}

.weight-card {
  border-radius: 8px; padding: 11px 13px; margin-bottom: 10px;
  border-left: 3px solid transparent; background: #f8f8f8;
  &.wc-blue  { border-left-color: #185fa5; }
  &.wc-amber { border-left-color: #ef9f27; }
  &.wc-green { border-left-color: #639922; }
  &.wc-red   { border-left-color: #e24b4a; }
  .wc-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; color: #b9b9c3; margin-bottom: 4px; }
  .wc-value { font-size: 16px; font-weight: 500; color: #5e5873; }
}

.status-badge { display: inline-flex; align-items: center; }
.excel-row-errors { font-size: 10px; color: #a32d2d; margin-top: 3px; max-width: 220px; }
.excel-preview-table ::v-deep td { vertical-align: middle; }

.section-head {
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.5px; color: #b9b9c3;
  padding-bottom: 8px; border-bottom: 0.5px solid #ebe9f1; margin-bottom: 12px;
}

.reason-label {
  display: flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 500; color: #5e5873; margin-bottom: 6px;
}
.required-star { color: #a32d2d; font-size: 14px; line-height: 1; }
.reason-hint   { font-size: 11px; font-weight: 400; color: #b9b9c3; }
.reason-error-msg {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; color: #a32d2d; margin-top: 5px;
}

.payments-table { font-size: 13px; }

::v-deep table thead th:first-child,
::v-deep table tbody td:first-child {
  text-align: center; vertical-align: middle;
  padding-left: 12px; padding-right: 12px;
}
</style>
